/**
 * Note: Updated Vuex store to ensure Storybook runs properly.
 * Vuex files are expected to be removed in DIC-2476,
 * so this update should prevent any issues with Storybook related to Vuex.
 */
import moment from 'moment'
import { createStore } from 'vuex'
import { Dependencies } from '../globals/utils'

let dataStoreInstance = null

/**
 * $dataStore instance getter and injection instead of
 * relying to Vue.globalProperties we can use provided
 * dependency from main
 */
function getDataStoreInstance () {
  if (!dataStoreInstance) {
    dataStoreInstance = Dependencies.inject('$dataStore')

    if (!dataStoreInstance) {
      throw new Error('Unable to inject $dataStore dependency')
    }
  }

  return dataStoreInstance
}

// Tags item component global state. This component is commonly used in media menu component in right section of the page.
const tagsComp = {
  state: {
    onLoadTags: [],
    updatedTags: {
      lastAction: null,
      component: null,
      tagNewVal: null,
      tagOldVal: null,
      tags: []
    }
  },
  mutations: {
    setOnLoadTags: (state, payload) => {
      state.onLoadTags = payload
    },
    updatedTags: (state, payload) => {
      state.updatedTags = payload
    },
    resetUpdatedTags: (state, payload) => {
      state.updatedTags = { lastAction: null, tags: [] }
    }
  },
  getters: {
    onLoadTags: state => state.onLoadTags,
    updatedTags: state => state.updatedTags
  },
  actions: {
    setOnLoadTags (context, payload) {
      context.commit('setOnLoadTags', payload)
    },
    updatedTags (context, payload) {
      context.commit('updatedTags', payload)
    },
    resetUpdatedTags (context, payload) {
      context.commit('resetUpdatedTags', payload)
    }
  }
}

// Tags slider component global state
const tagSliderComp = {
  state: {
    selectedTagsFromLocalStorage: []
  },
  mutations: {
    tagSliderUpdateSelectedTags: (state, payload) => {
      state.selectedTagsFromLocalStorage = payload
    }
  },
  getters: {
  },
  actions: {
    tagSliderUpdateSelectedTags (context, payload) {
      context.commit('tagSliderUpdateSelectedTags', payload)
    },
    cleanUpSelectedTagsFromLocalStorage (context, payload) {
      // Inject $dataStore dependency
      const $dataStore = getDataStoreInstance()
      const selectedTagsFromSlider = $dataStore?.getStore()?.selectedTagsFromSlider
        ? $dataStore.getStore().selectedTagsFromSlider
        : {}

      payload.data.forEach((target, indx) => {
        const selectedTags = selectedTagsFromSlider[target.name]

        if (selectedTags && selectedTags.length) {
          for (const i in selectedTags) {
            const selectedTag = selectedTags[i]
            const isSelectedTagExistFromCurrentList = target.currentTags.find((t) => {
              if (typeof (t) === 'string') {
                return t === selectedTag
              } else {
                return t.value === selectedTag
              }
            })

            if (!isSelectedTagExistFromCurrentList) selectedTags.splice(indx, 1)
          }
        }
      })

      $dataStore.setStore({ selectedTagsFromSlider })
      context.commit('tagSliderUpdateSelectedTags', selectedTagsFromSlider)
    }
  }
}

const playersComp = {
  state: {
    playerList: [],
    playersPageSelection: []
  },
  mutations: {
    setPlayerList: (state, payload) => {
      state.playerList = payload
    },
    updatePlayerSelection: (state, payload) => {
      state.playersPageSelection = payload
    }
  },
  getters: {
    playerList: state => state.playerList,
    playersPageSelection: state => state.playersPageSelection
  },
  actions: {
    setPlayerList (context, payload) {
      context.commit('setPlayerList', payload)
    },
    updatePlayerSelection (context, payload) {
      context.commit('updatePlayerSelection', payload)
    }
  }
}

const apiRequestsDataFormat = {
  requestId: null,
  requestName: '',
  error: null,
  originalData: null,
  status: null,
  responseData: null,
  requestType: null,
  retry: 0,
  startTime: '',
  endTime: ''
}
const apiRequests = {
  state: {
    data: []
  },
  mutations: {
    newApiRequest: (state, payload) => {
      if (!payload.requestId) return

      const stateData = state.data
      const exist = stateData.find((d) => { return d.requestId === payload.requestId })

      if (exist) return

      const newData = Object.assign({}, apiRequestsDataFormat)
      for (const dataKey in newData) {
        if (payload.dataKey) {
          newData[dataKey] = payload[dataKey]
        } else {
          newData[dataKey] = null
        }
      }

      stateData.push(newData)
    },
    updateApiRequest: (state, payload) => {
      if (!payload.requestId) return

      const stateData = [].concat(state.data)

      const findIndex = stateData.findIndex((d) => { return d.requestId === payload.requestId })
      const exist = findIndex >= 0

      if (!exist) return

      const targetRequestData = stateData[findIndex]
      targetRequestData.endTime = payload.endTime ? payload.endTime : null

      if (payload.status === 'success') {
        targetRequestData.responseData = payload.responseData ? payload.responseData : null
        targetRequestData.status = 'success'
      } else if (payload.status === 'error') {
        targetRequestData.responseData = null
        targetRequestData.status = 'error'
        targetRequestData.error = payload.error ? payload.error : true
      } else if (payload.deleteRequestData) {
        stateData.splice(findIndex, 1)
      }

      state.data = stateData
    }
  },
  getters: {
    requestData: state => state.data
  },
  actions: {
    newApiRequest (context, payload) {
      context.commit('newApiRequest', { ...payload, status: 'requesting', startTime: moment().format('hh:mm:ss') })
    },
    successApiRequest (context, payload) {
      context.commit('updateApiRequest', { ...payload, status: 'success', endTime: moment().format('hh:mm:ss') })

      window.setTimeout(() => {
        context.commit('updateApiRequest', { ...payload, deleteRequestData: true })
      }, 500)
    },
    failedApiRequest (context, payload) {
      context.commit('updateApiRequest', { ...payload, status: 'error', endTime: moment().format('hh:mm:ss') })
    }
  }
}

// Store for selected Player in Schedule Page component
const schedulePageSelectedPlayerComponent = {
  state: {
    scheduleItemFullDetails: []
  },
  mutations: {
    setScheduleItemFullDetails: (state, payload) => {
      state.scheduleItemFullDetails = payload
    }
  },
  getters: {
    scheduleItemFullDetails: state => state.scheduleItemFullDetails
  },
  actions: {
    setScheduleItemFullDetails (context, payload) {
      context.commit('setScheduleItemFullDetails', payload)
    }
  }
}

// Store for Media Files page component Page component
const mediaFilesPageComponent = {
  state: {
    mediFiles: []
  },
  mutations: {
  },
  getters: {
  },
  actions: {
  }
}

// global
const global = {
  state: {
    preferredTimeSystem: '24'
  },
  mutations: {
    setPreferredTimeSystem: (state, payload) => {
      state.preferredTimeSystem = payload
    }
  },
  getters: {
    preferredTimeFormat: state => state.preferredTimeSystem > 12 ? 'HH:mm' : 'hh:mm A'
  },
  actions: {
    setPreferredTimeSystem (context, payload) {
      context.commit('setPreferredTimeSystem', payload)

      // Inject $dataStore dependency
      const $dataStore = getDataStoreInstance()
      $dataStore.setPreferredTimeFormat(payload)
    }
  }
}

export const store = createStore({
  modules: {
    global,
    tagsComp,
    playersComp,
    tagSliderComp,
    apiRequests,
    schedulePageSelectedPlayerComponent,
    mediaFilesPageComponent
  }
})
